import React, { useReducer, useEffect, createContext, useCallback } from 'react'

import { CircularProgress, Box, Typography, makeStyles } from '@material-ui/core'

import { Logo } from './Logo'
import { FormSelectProduct } from './FormSelectProduct'
import { ProductSelected } from './ProductSelected'
import { ListOnlineStore } from './ListOnlineStores'

import api from '~/api'
import { useLiterals } from '~/components/Literals'
import { useDataEcommerce } from '~/components/Ecommerce/useDataEcommerce'
import useEcommerce from '~/components/Ecommerce/useEcommerce'

function reducer(state, { type, ...payload }) {
  return { ...state, ...payload }
}

export const OnlineStoreContext = createContext()

const useStyles = makeStyles((theme) => ({
  areaOnline: {
    flex: 1,
    width: '100%',
    backgroundColor: '#FFF',
    display: 'grid',
    minHeight: 0,
    gridTemplateColumns: 'repeat(3, 1fr)',
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: '1fr',
    },
  },
}))

export function trackApi(data, product, store, config, position) {
  api.clickTrack({
    gtin: product && product.gtin,
    productName: product && product.name,
    name: store ? store.name : 'all',
    cnpj: store ? store.cnpj : '00000000000000',
    brand: config.isOndetem ? product.brand : null,
    user: position?.user,
    ...data,
  })
}

export default function OnlineStorePage({ config, location, position }) {
  const classes = useStyles()

  const { getLiteral } = useLiterals()
  const { ...ecommerce } = useEcommerce()

  const [state, dispatch] = useReducer(reducer, {
    gtin: 'none',
    products: [],
    currentProduct: null,
    isPendingGtinInProducts: false,
    isLoadingProducts: false,

    openSheetPriceMonitoring: false,
    showMoreDescriptionProduct: false,
  })

  const getProductsByCustomer = useCallback(async () => {
    dispatch({ isLoadingProducts: true })

    const products = await api.getProducts(config.cnpj)

    const updatedProducts = products.map((product) => {
      const originalUrl = product.imageurl || ''
      const sanitizedUrl = originalUrl
        .replace('https://images.gofind.online/', 'https://s3-sa-east-1.amazonaws.com/gofind.img.repo/')
        .replace('https://images.pertinhodemim.com/', 'https://s3-sa-east-1.amazonaws.com/gofind.img.repo/')

      try {
        const url = new URL(sanitizedUrl)
        url.searchParams.set('height', '300')

        return {
          ...product,
          imageurl: url.toString(),
        }
      } catch (err) {
        // fallback em caso de URL inválida
        return {
          ...product,
          imageurl: `${sanitizedUrl}?height=300`,
        }
      }
    })

    dispatch({ products: updatedProducts, isLoadingProducts: false })
  }, [config.cnpj, dispatch])

  // Redirect when non active option "showBuyOnline". Prop is required for this page
  useEffect(() => {
    if (!config?.showBuyOnline) history.push('/404')
  }, [config])

  // Save log of type:ecommerce when accessing the screen
  useEffect(() => {
    const hasValidPosition = Boolean(position?.user?.lat && position?.user?.lng)
    const shouldTrack = config?.showBuyOnline && hasValidPosition && !location.search

    if (shouldTrack) {
      trackApi(
        {
          event: 'ecommerce',
          brandName: config.brandName,
        },
        null, // product
        null, // store
        config,
        position,
      )
    }
  }, [config?.brandName, config?.showBuyOnline, location.search, position?.user?.lat, position?.user?.lng])

  // Load products by customer CNPJ
  useEffect(() => {
    getProductsByCustomer(config.cnpj)
  }, [])

  const datasEcommerce = useDataEcommerce(ecommerce.dataEcommerce, config)

  return (
    <OnlineStoreContext.Provider value={{ ...state, ...datasEcommerce, dispatch, ecommerce, config, position }}>
      <Box
        position="absolute"
        width="100%"
        height="100vh"
        overflowY="auto"
        overflowX="hidden"
        zIndex={1200}
        display="flex"
        flexDirection="column"
        alignItems="center"
      >
        <Box width="100%" display="flex" flexDirection="column" alignItems="center" justifyContent="center">
          <Logo />
          <FormSelectProduct />
        </Box>

        {!state.currentProduct && (
          <Box
            width="100%"
            height="100vh"
            bgcolor="#FFF"
            paddingTop={3}
            display="flex"
            alignItems="center"
            flexDirection="column"
            gridGap={16}
            sx={{ '& > p': { textAlign: 'center' } }}
          >
            <img
              src="https://s3.sa-east-1.amazonaws.com/gofind.img.repo/productfinder/Grupo+415.svg"
              alt="Pesquisar produto"
              height="80px"
            />
            <Typography variant="body1">{getLiteral('2821')}</Typography>
          </Box>
        )}

        {ecommerce.loadingEcommerce && (
          <Box
            width="100%"
            height="100vh"
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            gridGap={16}
            bgcolor="#FFF"
          >
            <CircularProgress color="primary" style={{ fontSize: 40 }} />
            <Typography variant="body1" style={{ fontSize: 22 }}>
              {getLiteral('1503')}...
            </Typography>
          </Box>
        )}

        {state.currentProduct && !ecommerce.loadingEcommerce && (
          <Box className={classes.areaOnline}>
            <ProductSelected />
            <ListOnlineStore />
          </Box>
        )}
      </Box>
    </OnlineStoreContext.Provider>
  )
}
